import { WifiOff } from "@material-ui/icons";
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { renderToastUnique } from "utils/functions";

interface IClientConnectionStatusContext {
  connection: boolean;
  setConnection: Dispatch<SetStateAction<boolean>>;
}

interface IClientConnectionStatusProviderProps {
  children: ReactNode;
}

const ClientConnectionStatusContext = createContext<IClientConnectionStatusContext>(
  {} as IClientConnectionStatusContext,
);

export function ClientConnectionStatusProvider({
  children,
}: IClientConnectionStatusProviderProps) {
  const { t } = useTranslation("list-messages");
  const [connection, setConnection] = useState(navigator.onLine);

  const updateOnlineStatus = () => {
    setConnection(navigator.onLine);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      updateOnlineStatus();
    }, 30000);

    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);

    return () => {
      clearInterval(interval);
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
    };
  }, []);

  const createToast = useCallback<typeof renderToastUnique>(
    ({ toastId, message, type, options }) => {
      return renderToastUnique({ toastId, message, type, options });
    },
    [],
  );

  useEffect(() => {
    return connection
      ? toast.isActive("unique-toast-connection") &&
          createToast({
            toastId: "unique-toast-connection",
            message: t("connected-to-internet"),
            type: "success",
            options: {
              icon: null,
            },
          })
      : createToast({
          toastId: "unique-toast-connection",
          message: t("no-internet-check-connection"),
          type: "warning",
          options: {
            autoClose: false,
            closeButton: false,
            icon: <WifiOff style={{ color: "#ebcf17" }} />,
            closeOnClick: false,
            draggable: false,
          },
        });
  }, [connection]);

  const value = useMemo(
    () => ({
      connection,
      setConnection,
    }),
    [connection, setConnection],
  );

  return (
    <ClientConnectionStatusContext.Provider value={value}>
      {children}
    </ClientConnectionStatusContext.Provider>
  );
}

export function useClientConnectionStatus(): IClientConnectionStatusContext {
  const context = useContext(ClientConnectionStatusContext);

  if (!context) {
    throw new Error(
      "useClientConnectionStatus must be used within an ClientConnectionStatusProvider",
    );
  }

  return context;
}
